






























































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import ApiClient from '@/services/api-client'
import { Account, App, EditorialProject, Permission } from '@/models'
import { AccountUtils } from '@/utils/account-utils'

@Component({
  components: {}
})
export default class CopyPermissionsModal extends Vue {
  @Prop({ required: true }) targetAccountId!: string
  @Prop({ required: true }) currentPermissions!: Permission[]
  @Prop({ required: true }) apps!: App[]
  @Prop({ required: true }) editorialProjects!: EditorialProject[]

  private sourceAccount: Account | null = null
  private searchResults: Account[] = []
  private searchLoading = false
  private loading = false
  private sourcePermissions: Permission[] = []
  private loadingPreview = false

  get preview(): { appId: string; appName: string; permissions: Permission[] }[] {
    if (!this.sourceAccount || this.loadingPreview) return []
    const existingKeys = new Set(this.currentPermissions.map(p => `${p.appId}|${p.editorialProjectId ?? ''}`))
    const newPermissions = this.sourcePermissions.filter(
      p => !existingKeys.has(`${p.appId}|${p.editorialProjectId ?? ''}`)
    )
    const grouped = new Map<string, Permission[]>()
    for (const p of newPermissions) {
      if (!grouped.has(p.appId)) grouped.set(p.appId, [])
      grouped.get(p.appId)!.push(p)
    }
    return Array.from(grouped.entries()).map(([appId, perms]) => ({
      appId,
      appName: this.apps.find(a => a.appId === appId)?.name || appId,
      permissions: perms
    }))
  }

  get newCount(): number {
    return this.preview.reduce((sum, g) => sum + g.permissions.length, 0)
  }

  @Watch('sourceAccount')
  async onSourceAccountChange(account: Account | null) {
    this.sourcePermissions = []
    if (!account) return
    this.loadingPreview = true
    try {
      const fetched = await ApiClient.getAccount(account.accountId)
      this.sourcePermissions = fetched.permissions || []
    } catch (error) {
      console.error('Error loading source account permissions:', error)
    } finally {
      this.loadingPreview = false
    }
  }

  async onSearch(search: string, loading: (v: boolean) => void) {
    if (!search || search.length < 2) {
      this.searchResults = []
      return
    }
    loading(true)
    try {
      const result = await ApiClient.searchAccounts(search, 20, 0)
      this.searchResults = result.items.filter(a => a.accountId !== this.targetAccountId)
    } catch (error) {
      console.error('Error searching accounts:', error)
    } finally {
      loading(false)
    }
  }

  async confirm(bvModalEvent: any) {
    bvModalEvent.preventDefault()
    if (!this.sourceAccount) return
    this.loading = true
    try {
      await ApiClient.copyPermissions(this.targetAccountId, this.sourceAccount.accountId)
      this.$bvToast.toast(this.$t('account_detail.permissions.messages.copy_permissions_success').toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true
      })
      this.$bvModal.hide('copy-permissions-modal')
      this.$emit('permissions-copied')
    } catch (error: any) {
      console.error('Error copying permissions:', error)
      this.$bvToast.toast(error.message || this.$t('account_detail.permissions.messages.copy_permissions_error').toString(), {
        title: this.$t('account_detail.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    } finally {
      this.loading = false
    }
  }

  reset() {
    this.sourceAccount = null
    this.searchResults = []
    this.sourcePermissions = []
  }

  getRoleVariant(role: string): string {
    return AccountUtils.getRoleVariant(role)
  }

  getEditorialProjectName(projectId: string | null): string {
    if (!projectId) return 'N/A'
    const project = this.editorialProjects.find(ep => ep.id === projectId)
    return project ? project.name : projectId
  }
}
